/**
 * Single access point for `window.Telegram.WebApp`. Returns null in non-TG envs
 * (browser dev) so components can render gracefully without the SDK.
 */

import { useEffect, useRef, useState } from 'react';
import type WebApp from '@twa-dev/sdk';

type TgWebApp = typeof WebApp;

export function getTg(): TgWebApp | null {
  if (typeof window === 'undefined') return null;
  return window.Telegram?.WebApp ?? null;
}

export function useTg(): TgWebApp | null {
  const ref = useRef<TgWebApp | null>(null);
  if (ref.current === null) ref.current = getTg();
  return ref.current;
}

/** Subscribes to TG `themeChanged` event and returns colorScheme. */
export function useTgTheme(): 'light' | 'dark' {
  const tg = useTg();
  const [scheme, setScheme] = useState<'light' | 'dark'>(() =>
    tg?.colorScheme === 'light' ? 'light' : 'dark',
  );
  useEffect(() => {
    if (!tg) return;
    const handler = (): void => {
      setScheme(tg.colorScheme === 'light' ? 'light' : 'dark');
    };
    tg.onEvent('themeChanged', handler);
    return () => tg.offEvent('themeChanged', handler);
  }, [tg]);
  return scheme;
}
